import { useState, useLayoutEffect, useEffect, useRef } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import dark_theme from "@amcharts/amcharts5/themes/Dark";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export const PayoffChart = ({ payoffData, futLtp, isLoading, error, oldFutLtp }) => {
  const [chartRoot, setChartRoot] = useState(null);
  const strikeRangeRef = useRef(null);
  const oldStrikeRangeRef = useRef(null);
  const seriesMapRef = useRef(new Map());

  useLayoutEffect(() => {
    const root = am5.Root.new("payoff-chart", {});
    root.setThemes([dark_theme.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "zoomX",
        wheelY: "zoomY",
        paddingTop: 20,
        paddingRight: 15,
        paddingBottom: 30,
        paddingLeft: 15,
        layout: root.verticalLayout
      })
    );

    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {
          minGridDistance: 50
        })
      })
    );

    xAxis.get("renderer").labels.template.setAll({
      rotation: -45,
      centerY: am5.p50,
      centerX: am5.p100
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: 50
        })
      })
    );

    const zeroRange = yAxis.createAxisRange(
      yAxis.makeDataItem({ value: 0 })
    );

    zeroRange.get("grid").setAll({
      stroke: am5.color("#ffffff"),
      strokeDasharray: [5, 5],
      strokeOpacity: 0.8
    });

    chart.set("cursor", am5xy.XYCursor.new(root, {
      behavior: "zoomXY"
    }));

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.p50,
        x: am5.p50
      })
    );

    xAxis.set("tooltip", am5.Tooltip.new(root, {
      themeTags: ["axis"]
    }));

    yAxis.set("tooltip", am5.Tooltip.new(root, {
      themeTags: ["axis"]
    }));

    setChartRoot({ root, chart, xAxis, yAxis, legend });

    return () => {
      root.dispose();
    };
  }, []);

  useEffect(() => {
    if (payoffData && payoffData.data && chartRoot) {
      console.log('Updating chart with data:', payoffData);
      let currentDate = payoffData.current_date;
      let currentDateTime = payoffData.current_date;
      currentDate = dayjs.tz(currentDate, "DD-MM-YYYY", "Asia/Kolkata");
      currentDateTime = dayjs.tz(payoffData.current_date + " " + payoffData.current_time, "DD-MM-YYYY HH:mm", "Asia/Kolkata");

      const dateKeys = Object.keys(payoffData.data).filter(key => 
        Array.isArray(payoffData.data[key])
      );
      
      const currentSeriesKeys = Array.from(seriesMapRef.current.keys());
      currentSeriesKeys.forEach(key => {
        if (!dateKeys.includes(key)) {
          const series = seriesMapRef.current.get(key);
          chartRoot.chart.series.removeValue(series);
          series.dispose();
          seriesMapRef.current.delete(key);
        }
      });

      dateKeys.forEach((dateKey, index) => {
        let series = seriesMapRef.current.get(dateKey);
          const lineStyle = {strokeWidth: 3 };
          let color = "#ccff66";
          console.log("dateKey", dateKey, dayjs.tz(dateKey, "DD-MM-YYYY HH:mm", "Asia/Kolkata"))
          const parsedDate = dayjs.tz(dateKey, "DD-MM-YYYY HH:mm", "Asia/Kolkata");
          console.log("dates", currentDateTime, parsedDate, parsedDate.isBefore(currentDateTime), parsedDate.isSame(currentDateTime), parsedDate.isAfter(currentDateTime))
          if (parsedDate.isBefore(currentDateTime)) {
            // lineStyle.strokeDasharray = [5, 5];
            color = "#ff8000"; // Orange
          } else if (parsedDate.isAfter(currentDateTime)) {
            // lineStyle.strokeDasharray = [5, 5];
            color = "#0099ff"; // Blue
          }else if (parsedDate.isSame(currentDateTime)) {
            lineStyle.strokeDasharray = [5, 3];
            color = "#ffffff"; // White
          }
          console.log(index, dateKey, lineStyle, color);
        if (!series) {

          series = chartRoot.chart.series.push(
            am5xy.LineSeries.new(chartRoot.root, {
              name: dateKey,
              xAxis: chartRoot.xAxis,
              yAxis: chartRoot.yAxis,
              stroke: am5.color(color),
              valueYField: "total_profit_loss",
              valueXField: "target_index_price",
              tooltip: am5.Tooltip.new(chartRoot.root, {
                labelText: `${dateKey}\nPnL: Rs. {valueY}\nLTP value: {ltp_based_total_profit_loss}\nFutures: {valueX}`
              })
            })
          );
          series.strokes.template.setAll({
            stroke: am5.color(color),
            strokeWidth: lineStyle.strokeWidth,
            strokeDasharray: lineStyle.strokeDasharray
          });

          seriesMapRef.current.set(dateKey, series);
        }

        if (payoffData.data[dateKey] && Array.isArray(payoffData.data[dateKey])) {
          series.data.setAll(payoffData.data[dateKey]);
        }
      });

      chartRoot.legend.data.setAll(chartRoot.chart.series.values);


      if (oldFutLtp && chartRoot.xAxis) {
        if (oldStrikeRangeRef.current) {
          oldStrikeRangeRef.current.dispose();
        }

        const strikeRange = chartRoot.xAxis.createAxisRange(
          chartRoot.xAxis.makeDataItem({ value: oldFutLtp })
        );

        strikeRange?.get("grid")?.setAll({
          stroke: am5.color("#ff8000"),
          strokeWidth: 2,
          strokeOpacity: 0.8
        });

        oldStrikeRangeRef.current = strikeRange;
      }
      if (futLtp && chartRoot.xAxis) {
        if (strikeRangeRef.current) {
          strikeRangeRef.current.dispose();
        }

        const strikeRange = chartRoot.xAxis.createAxisRange(
          chartRoot.xAxis.makeDataItem({ value: futLtp })
        );

        strikeRange?.get("grid")?.setAll({
          stroke: am5.color("#ffffff"),
          strokeWidth: 2,
          strokeOpacity: 0.9,
          strokeDasharray: [5, 3]
        });

        strikeRangeRef.current = strikeRange;
      }
    }
  }, [payoffData, futLtp, chartRoot]);

  return (
    <div style={{
      backgroundColor: "#111111",
      padding: "20px",
      width: "100%",
      minHeight: "800px"
    }}>
      {/* Loading State */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '20px', color: 'white' }}>
          <p>Loading payoff data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{ color: 'red', padding: '10px', border: '1px solid red', margin: '10px 0' }}>
          <h3> {error["response"]? "Error: "+  error["response"]["data"] : error["message"] + ". Please refresh and try again in few mins."}</h3>
        </div>
      )}

      <div
        id="payoff-chart"
        style={{
          width: "100%",
          height: "100%"
        }}
      />
    </div>
  );
};